import { Controller } from "@hotwired/stimulus";
import debounce from "../lib/debounce";

export default class extends Controller {
  static targets = ["treeData", "scenarioConfig", "treeSummary", "scenarioSummary"];
  static values = { previewUrl: String };

  connect() {
    this.debouncedUpdate = debounce(this.updatePreview.bind(this), 2000);
    this.updatePreview();

    this.handleTopologySaved = this.topologySaved.bind(this);
    document.addEventListener("topology-editor:saved", this.handleTopologySaved);
  }

  disconnect() {
    document.removeEventListener("topology-editor:saved", this.handleTopologySaved);
  }

  topologySaved(event) {
    if (!this.hasTreeDataTarget) return;

    const json = JSON.stringify(event.detail.treeData, null, 2);
    this.treeDataTarget.value = json;
    this.treeDataTarget.dispatchEvent(new Event("input", { bubbles: true }));
    this.updatePreview();
  }

  changed() {
    this.debouncedUpdate();
    if (!this.debouncedSyncVisual) {
      this.debouncedSyncVisual = debounce(this.syncVisualEditor.bind(this), 2000);
    }
    this.debouncedSyncVisual();
  }

  syncVisualEditor() {
    if (!this.hasTreeDataTarget) return;

    const data = this.parseJson(this.treeDataTarget.value);
    if (!data || !data.grid) return;

    document.dispatchEvent(
      new CustomEvent("topology-data:changed", {
        bubbles: true,
        detail: { treeData: data },
      }),
    );
  }

  async updatePreview() {
    if (this.hasPreviewUrlValue && this.previewUrlValue) {
      await this.fetchPreview();
    } else {
      this.updatePreviewLocally();
    }
  }

  async fetchPreview() {
    const treeData = this.hasTreeDataTarget ? this.treeDataTarget.value : "{}";
    const scenarioConfig = this.hasScenarioConfigTarget ? this.scenarioConfigTarget.value : "{}";

    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content;

    try {
      const response = await fetch(this.previewUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({ tree_data: treeData, scenario_config: scenarioConfig }),
      });

      if (!response.ok) {
        const error = await response.json();
        this.showError(error.error || "Preview failed");
        return;
      }

      const { description } = await response.json();

      if (this.hasTreeSummaryTarget) {
        this.treeSummaryTarget.textContent = description || "";
        this.treeSummaryTarget.classList.remove("text-message-heading-error");
        this.treeSummaryTarget.classList.add("text-text-secondary");
      }
      if (this.hasScenarioSummaryTarget) {
        this.scenarioSummaryTarget.textContent = "";
        this.scenarioSummaryTarget.classList.remove("text-message-heading-error");
        this.scenarioSummaryTarget.classList.add("text-text-secondary");
      }
    } catch {
      this.updatePreviewLocally();
    }
  }

  showError(message) {
    if (this.hasTreeSummaryTarget) {
      this.treeSummaryTarget.textContent = message;
      this.treeSummaryTarget.classList.add("text-message-heading-error");
      this.treeSummaryTarget.classList.remove("text-text-secondary");
    }
  }

  updatePreviewLocally() {
    this.updateTreeSummary();
    this.updateScenarioSummary();
  }

  updateTreeSummary() {
    if (!this.hasTreeSummaryTarget || !this.hasTreeDataTarget) return;

    const summary = this.summarizeTree();
    if (this.treeParseError) {
      this.treeSummaryTarget.textContent = this.treeParseError;
      this.treeSummaryTarget.classList.add("text-message-heading-error");
      this.treeSummaryTarget.classList.remove("text-text-secondary");
    } else {
      this.treeSummaryTarget.textContent = summary || "";
      this.treeSummaryTarget.classList.remove("text-message-heading-error");
      this.treeSummaryTarget.classList.add("text-text-secondary");
    }
  }

  updateScenarioSummary() {
    if (!this.hasScenarioSummaryTarget || !this.hasScenarioConfigTarget) return;

    const summary = this.summarizeScenario();
    if (this.scenarioParseError) {
      this.scenarioSummaryTarget.textContent = this.scenarioParseError;
      this.scenarioSummaryTarget.classList.add("text-message-heading-error");
      this.scenarioSummaryTarget.classList.remove("text-text-secondary");
    } else {
      this.scenarioSummaryTarget.textContent = summary ? `Scenario: ${summary}` : "";
      this.scenarioSummaryTarget.classList.remove("text-message-heading-error");
      this.scenarioSummaryTarget.classList.add("text-text-secondary");
    }
  }

  summarizeTree() {
    this.treeParseError = null;
    const data = this.parseJson(this.treeDataTarget.value);
    if (!data) {
      this.treeParseError = this.lastParseError;
      return null;
    }
    if (!data.grid) {
      this.treeParseError = "Missing 'grid' key";
      return null;
    }
    return data.grid.name || "";
  }

  summarizeScenario() {
    this.scenarioParseError = null;
    const data = this.parseJson(this.scenarioConfigTarget.value);
    if (!data) {
      this.scenarioParseError = this.lastParseError;
      return null;
    }
    if (!data.sessions || !Array.isArray(data.sessions)) return null;
    if (data.sessions.length === 0) return null;

    const totalKwh = data.sessions.reduce((sum, s) => sum + (s.energy_kwh || 0), 0);
    return `${data.sessions.length} sessions, ${Math.round(totalKwh)} kWh`;
  }

  parseJson(text) {
    this.lastParseError = null;
    if (!text || text.trim() === "") return null;
    try {
      return JSON.parse(text);
    } catch (e) {
      this.lastParseError = e.message;
      return null;
    }
  }
}
